import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/tableStyles.css';
import {
  Container,
  Box,
  Typography,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Backdrop,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material';
import { 
  Close as CloseIcon,
  HelpOutline as HelpOutlineIcon,
  Save as SaveIcon,
  RestartAlt as RestartAltIcon,
  RotateLeft as UndoIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import SearchForm from '../components/search/SearchForm';
import AnalyticsSection from '../components/search/AnalyticsSection';
import MapViewContainer from '../components/maps/MapViewContainer';
import logger from '../utils/logger';
import { useSearchData } from '../hooks/useSearchData';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { useResultsFilter } from '../hooks/useResultsFilter';
import { useFlagMaps } from '../hooks/useFlagMaps';
import pdfReportService from '../services/pdfReportService';
import { INK, MUTED, LINE, goldButtonSx } from '../constants/brand';

// ============================================================================
// MAIN SEARCH COMPONENT
// ============================================================================
const Search = () => {
  const { isAuthenticated, token, user, tierInfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { dataset } = useParams();
  
  // Redirect if no dataset or invalid dataset
  useEffect(() => {
    if (!dataset) {
      navigate('/app');
      return;
    }
    // Validate dataset (currently only 'texas' is supported)
    const validDatasets = ['texas'];
    if (!validDatasets.includes(dataset.toLowerCase())) {
      navigate('/app');
      return;
    }
  }, [dataset, navigate]);


  // ============================================================================
  // NOTIFICATION SYSTEM
  // ============================================================================
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info', duration: 6000 });

  const showNotification = (message, severity = 'info') => {
    // Special duration for limit errors (longer message needs more time to read)
    const duration = message.includes('total rows') && message.includes('exceeds') ? 20000 : 6000;
    setNotification({ open: true, message, severity, duration });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // ============================================================================
  // PDF REPORT GENERATION
  // ============================================================================
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportProgress, setReportProgress] = useState('');
  const [isCancellingReport, setIsCancellingReport] = useState(false);

  // ============================================================================
  // SAVE SEARCH DIALOG
  // ============================================================================
  const [saveSearchDialogOpen, setSaveSearchDialogOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [savingSearch, setSavingSearch] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Additional dialog states
  const [aboutSearchDialogOpen, setAboutSearchDialogOpen] = useState(false);
  const [refineSearchDialogOpen, setRefineSearchDialogOpen] = useState(false);
  const [editSearchDialogOpen, setEditSearchDialogOpen] = useState(false);
  
  // Search form visibility state
  const [searchFormVisible, setSearchFormVisible] = useState(true);
  const [wasRefineSearch, setWasRefineSearch] = useState(false);
  const [originalSearchParams, setOriginalSearchParams] = useState(null);
  
  // Analytics panel state for map view
  const [analyticsPanelOpen, setAnalyticsPanelOpen] = useState(true);

  // Refs to store callbacks for syncing selection clearing between Map and Analytics
  const clearMapSelectionRef = useRef(null);
  const clearAnalyticsSelectionRef = useRef(null);
  const zoomToDataRef = useRef(null); // Ref to trigger zoom from Analytics

  // Refs for lease navigation between Map and Summary
  const navigateToLeaseInSummaryRef = useRef(null);
  const navigateToLeaseInMapRef = useRef(null);

  // Function to clear Analytics selection (called from Map)
  const handleClearAnalyticsSelection = () => {
    if (clearAnalyticsSelectionRef.current) {
      clearAnalyticsSelectionRef.current();
    }
  };

  // ============================================================================
  // SEARCH FORM EXPAND/COLLAPSE
  // ============================================================================
  const [searchFormExpanded, setSearchFormExpanded] = useState(true);

  // Cancel PDF report generation
  const handleCancelReport = () => {
    setIsCancellingReport(true);
    setReportProgress('Cancelling PDF generation...');
    
    // Stop the PDF generation process
    pdfReportService.cancelGeneration();
    
    // Reset states after a brief delay
    setTimeout(() => {
      setIsGeneratingReport(false);
      setIsCancellingReport(false);
      setReportProgress('');
    }, 500);
  };

  // Generate PDF report
  const handleGenerateReport = async () => {
    if (!searchResults) {
      showNotification('No search data available to generate report', 'error');
          return;
    }

    setIsGeneratingReport(true);
    setIsCancellingReport(false);
    setReportProgress('Initializing PDF generation...');
    
    try {
      const filterActive = Boolean(resultsFilter?.isFilterActive);
      const filtered = resultsFilter?.filteredResults || searchResults;

      // Full search for Contents "Data Tables Included"; filtered drives chapters when filter is on
      const searchDataWithUser = {
        data: searchResults.data,
        counts: searchResults.counts,
        searchParams: searchParams,
        activityDateFilters,
        wellDateFilters,
        user: { email: user?.email || 'Unknown User' },
        filter: {
          isActive: filterActive,
          selectedLeaseKeys: filterActive
            ? Array.from(resultsFilter.selectedLeaseKeys || [])
            : [],
          data: filterActive ? filtered?.data : null,
          counts: filterActive ? filtered?.counts : null,
        },
      };
      
      const result = await pdfReportService.generateReport(searchDataWithUser, {}, (progress) => {
        if (!isCancellingReport) {
          setReportProgress(progress);
        }
      });
      
      if (!result.success) {
        if (!isCancellingReport) {
          // Check if it's a cancellation vs actual error
          if (result.error === 'PDF generation cancelled') {
            showNotification('PDF generation cancelled', 'info');
    } else {
            showNotification(`Error generating report: ${result.error}`, 'error');
          }
      }
    } else {
        if (!isCancellingReport) {
          showNotification('PDF report generated successfully!', 'success');
        }
      }
        } catch (error) {
      if (!isCancellingReport) {
        // Check if it's a cancellation vs actual error
        if (error.message === 'PDF generation cancelled') {
          showNotification('PDF generation cancelled', 'info');
    } else {
          showNotification('Error generating report. Please try again.', 'error');
        }
      }
    } finally {
      if (!isCancellingReport) {
        setIsGeneratingReport(false);
        setReportProgress('');
      }
    }
  };

  // Save search function
   const handleSaveSearch = async () => {
     if (!saveSearchName.trim()) {
       setSaveError('Please enter a search name');
       return;
     }
     
     setSavingSearch(true);
     setSaveError('');
     setSaveSuccess(false);
     
     try {
       const searchData = {
         search_name: saveSearchName.trim(),
         description: null,
         state: searchParams.state,
                           search_params_json: {
                    ...searchParams,
          // Include consolidated date filters
          activityDateFilters: activityDateFilters,
          wellDateFilters: wellDateFilters,
          // Include well group filters
          wellGroupFilters: wellGroupFilters,
          // Include UI state
          dateFiltersExpanded: dateFiltersExpanded,
          mainFiltersExpanded: mainFiltersExpanded,
          otherFiltersExpanded: otherFiltersExpanded,
          multiLineEnabled: multiLineEnabled
                  }
       };
       
       const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
       const response = await fetch(`${API_BASE_URL}/saved-searches`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`,
         },
         body: JSON.stringify(searchData),
       });
       
       if (!response.ok) {
         throw new Error('Failed to save search');
       }
       
       // Clear the form and show success
       setSaveSearchName('');
       setSaveSuccess(true);
       setSaveError('');
       
       // Dialog will remain open with Close button - user can close manually
     } catch (err) {
       setSaveError(err.message);
       setSaveSuccess(false);
     } finally {
       setSavingSearch(false);
     }
   };

  // ============================================================================
  // CUSTOM HOOKS
  // ============================================================================
  const analyticsResetRef = useRef(null);
  const searchData = useSearchData(token, showNotification, () => analyticsResetRef.current?.(), dataset);

  // Calculate flag maps immediately when search results load (for map display)
  const { leaseFlagsMap, countyFlagsMap, redFlagsCache } = useFlagMaps(
    searchData.searchResults,
    searchData.searchParams,
    null // No pre-built countyMap - hook will build its own using buildCountyMap utility
  );
  
  // ============================================================================
  // RESULTS FILTERING
  // ============================================================================
  const resultsFilter = useResultsFilter(searchData.searchResults);
  
  const analyticsData = useAnalyticsData(resultsFilter.filteredResults || searchData.searchResults, searchData.searchParams);

  // Function to navigate from Map to Summary tab for a specific lease
  const handleNavigateToLeaseInSummary = useCallback((leaseKey, lease) => {
    // Don't open analytics panel - popup dialogs work independently
    // Don't change anything in analytics if it's already open - just open the lease card
    const wasAnalyticsOpen = analyticsPanelOpen;
    
    // Use a small delay if analytics was open (to ensure any pending state updates complete)
    // Pass skipViewModeSwitch=true if analytics was already open (don't change view mode or tab)
    setTimeout(() => {
      if (navigateToLeaseInSummaryRef.current) {
        navigateToLeaseInSummaryRef.current(leaseKey, lease, wasAnalyticsOpen);
      }
    }, analyticsPanelOpen ? 100 : 0);
  }, [analyticsPanelOpen]);
  
  // Update the reset function reference
  useEffect(() => {
    analyticsResetRef.current = analyticsData.resetAllSorting;
  }, [analyticsData.resetAllSorting]);

  // Destructure search data
  const {
    searchResults,
    loading,
    searchParams,
    inputValues,
    activityDateFilters,
    wellDateFilters,
    wellGroupFilters,
    mainFiltersExpanded,
    otherFiltersExpanded,
    dateFiltersExpanded,
    multiLineEnabled,
    handleSearch,
    handleReset,
    cancelSearch,
    handleInputChange,
    handleInputBlur,
    toggleWellGroupFilter,
    toggleMultiLine,
    validateSearchParameters,
    setSearchParams,
    setInputValues,
    setActivityDateFilters,
    setWellDateFilters,
    setWellGroupFilters,
    setMainFiltersExpanded,
    setOtherFiltersExpanded,
    setDateFiltersExpanded,
    setMultiLineEnabled,
  } = searchData;

  // Destructure analytics data
  const {
    activeTab,
    wellGroupingTableSort,
    riskWellsTableSort,
    multipleRecordsTableSort,
    violationsByRuleTableSort,
    riskWellsFilter,
    violationsFilter,
    severancesFilter,
    inspectionsFilter,
    inactiveYearsBinSize,
    hiddenWellGroups,
    hiddenViolationsLines,
    hiddenReinspectionLines,
    hiddenSeverancesLines,
    hiddenSeveranceVolumeLines,
    hiddenInspectionLines,
    hiddenProductionLines,
    hiddenFlaringLines,
    hiddenFuelGasLines,
    hiddenSpillLines,
    hiddenSpillVolumeLines,
    getCurrentTableType,
    getCurrentTableData,
    getCurrentTableCounts,
    handleWellGroupingTableSort,
    handleRiskWellsTableSort,
    handleMultipleRecordsTableSort,
    handleViolationsByRuleTableSort,
    resetRiskWellsFilter,
    resetViolationsFilter,
    resetSeverancesFilter,
    resetInspectionsFilter,
    resetInactiveYearsBinSize,
    resetHiddenWellGroups,
    resetHiddenViolationsLines,
    resetHiddenReinspectionLines,
    resetHiddenSeverancesLines,
    resetHiddenSeveranceVolumeLines,
    resetHiddenInspectionLines,
    resetHiddenProductionLines,
    resetHiddenFlaringLines,
    resetHiddenFuelGasLines,
    resetHiddenSpillLines,
    resetHiddenSpillVolumeLines,
    getAllUniqueOperatorNames,
  } = analyticsData;

  // Hide Search Params after search completes (but not after refine)
  useEffect(() => {
    if (searchResults && !loading) {
      // Store original search parameters for reset functionality
      setOriginalSearchParams({ ...searchParams });
      
      // Only hide if this wasn't triggered by refine search
      if (!wasRefineSearch) {
        setSearchFormVisible(false);
      }
      // Reset the refine search flag
      setWasRefineSearch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, loading, wasRefineSearch]);

  // UI state
  const [operatorNamesDialogOpen, setOperatorNamesDialogOpen] = useState(false);
  
  // Function to clear location state (for reset functionality)
  const clearLocationState = () => {
    window.history.replaceState({}, document.title);
  };
  
  // Wrapper for reset that also clears location state and expands form
  const handleResetWrapper = () => {
    clearLocationState();
    handleReset();
    setSearchFormExpanded(true); // Expand form when resetting
    setSearchFormVisible(true); // Show Search Params when resetting
    setWasRefineSearch(false); // Reset refine search flag
    setActivityDateInfoOpen(true); // Re-show default activity date notice
  };
  
  // Search execution (refine + main Search button)
  const runSearch = () => {
    clearLocationState();
    if (resultsFilter) {
      resultsFilter.clearFilter();
    }
    handleSearch();
  };

  const handleSearchWrapper = () => {
    runSearch();
  };

  // Info dialog for default activity date range (shown on new search page / reset)
  const [activityDateInfoOpen, setActivityDateInfoOpen] = useState(false);

  useEffect(() => {
    // Skip when loading a saved search; show for a fresh Search page
    if (location.state?.loadSavedSearch) {
      setActivityDateInfoOpen(false);
      return;
    }
    // Skip if restoring an in-progress session with results
    try {
      const raw = sessionStorage.getItem('diligence_desk_search_session');
      if (raw) {
        const sessionData = JSON.parse(raw);
        if (sessionData?.searchResults?.data) {
          setActivityDateInfoOpen(false);
          return;
        }
      }
    } catch (_) { /* ignore */ }
    setActivityDateInfoOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatActivityDateLabel = (dateStr) => {
    if (!dateStr || !dateStr.includes('-')) return null;
    const parts = dateStr.split('-');
    if (parts.length < 2) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    if (Number.isNaN(year) || Number.isNaN(month)) return null;
    const date = new Date(year, month, parts[2] ? parseInt(parts[2], 10) : 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const activityStartLabel = formatActivityDateLabel(activityDateFilters?.start_date);
  const activityEndLabel = formatActivityDateLabel(activityDateFilters?.end_date);
  let activityDateInfoMessage =
    'No activity date range is set. Production, Inspections, Violations, Severances, and Spills will include all available history.';
  if (activityStartLabel && activityEndLabel) {
    activityDateInfoMessage =
      `By default, activity datasets (Production, Inspections, Violations, Severances, and Spills) are limited to a date range of ${activityStartLabel} – ${activityEndLabel} (~24 months of history). You can change this under Date Filters before searching.`;
  } else if (activityStartLabel) {
    activityDateInfoMessage =
      `By default, activity datasets include records from ${activityStartLabel} onward. You can change this under Date Filters before searching.`;
  } else if (activityEndLabel) {
    activityDateInfoMessage =
      `By default, activity datasets include records through ${activityEndLabel}. You can change this under Date Filters before searching.`;
  }

  const activityDateConfirmDialog = (
    <Dialog
      open={activityDateInfoOpen}
      onClose={() => setActivityDateInfoOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Default Activity Date Range</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {activityDateInfoMessage}
        </DialogContentText>
        <DialogContentText sx={{ mt: 2 }}>
          Wells are not limited by this activity date filter.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={() => setActivityDateInfoOpen(false)} autoFocus>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
  
  // Wrapper for save search that also clears location state
  const handleSaveSearchWrapper = () => {
    clearLocationState();
    handleSaveSearch();
  };

  
  // Data arrays (derived from filtered search results)
  const filteredSearchResults = resultsFilter.filteredResults || searchResults;
  const wellsData = filteredSearchResults?.data?.wells || [];
  const productionData = filteredSearchResults?.data?.production || [];
  const inspectionsData = filteredSearchResults?.data?.inspections || [];
  const violationsData = filteredSearchResults?.data?.violations || [];
  const severancesData = filteredSearchResults?.data?.severances || [];
  const spillsData = filteredSearchResults?.data?.spills || [];

  // Auto-detect columns from data
  const autoDetectColumns = (data, tableType) => {
    if (!data || data.length === 0) return;
    // This function can be implemented if needed for column management
  };

  // Format table result for display with limit information
  const formatTableResult = (tableName, count, limit) => {
    const isAtLimit = count >= limit;
    const countFormatted = count.toLocaleString();
    
    if (isAtLimit) {
      return `${tableName} — ${countFormatted} records (limited to first ${limit.toLocaleString()})`;
    } else {
      return `${tableName} — ${countFormatted} records`;
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================
  // Load session data on component mount (only if not loading a saved search)
  useEffect(() => {
    if (!location.state?.loadSavedSearch) {
      // For normal navigation to search page, restore session data
      searchData.loadSessionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.loadSavedSearch, searchData.loadSessionData]);

  // Auto-detect columns when search results change
  useEffect(() => {
    if (searchResults?.data && Object.keys(searchResults.data).length > 0) {
      // Only run if we have actual search results with data
      autoDetectColumns(productionData, 'production');
      autoDetectColumns(wellsData, 'wells');
      autoDetectColumns(inspectionsData, 'inspections');
      autoDetectColumns(violationsData, 'violations');
      autoDetectColumns(severancesData, 'severances');
      autoDetectColumns(spillsData, 'spills');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  // Auto-collapse search form when search completes successfully
  useEffect(() => {
    if (searchResults?.data && Object.keys(searchResults.data).length > 0 && !loading) {
      // Search completed successfully - collapse the form and scroll to top
      setSearchFormExpanded(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [searchResults, loading]);

  // Handle loading saved searches from navigation
  useEffect(() => {
    if (location.state?.loadSavedSearch && location.state?.searchParams) {
      // Clear session data first to prevent conflicts
      searchData.clearSearchSession();
      setActivityDateInfoOpen(false);
      
      // Add a small delay to ensure session data is cleared
      setTimeout(() => {
      let savedParams = location.state.searchParams;
      
      // If searchParams is a string, parse it first
      if (typeof savedParams === 'string') {
        try {
          savedParams = JSON.parse(savedParams);
        } catch (error) {
          logger.error('Error parsing JSON:', error);
          return;
        }
      }
      
      // Convert arrays back to newline-separated strings for display
      const convertedParams = {
        ...savedParams,
        operator_name: savedParams.operator_name ? (Array.isArray(savedParams.operator_name) ? savedParams.operator_name.join('\n') : savedParams.operator_name) : '',
        operator_number: savedParams.operator_number ? (Array.isArray(savedParams.operator_number) ? savedParams.operator_number.join('\n') : savedParams.operator_number) : '',
        lease_name: savedParams.lease_name ? (Array.isArray(savedParams.lease_name) ? savedParams.lease_name.join('\n') : savedParams.lease_name) : '',
        lease_number: savedParams.lease_number ? (Array.isArray(savedParams.lease_number) ? savedParams.lease_number.join('\n') : savedParams.lease_number) : '',
        county: savedParams.county ? (Array.isArray(savedParams.county) ? savedParams.county.join('\n') : savedParams.county) : '',
        api_number: savedParams.api_number ? (Array.isArray(savedParams.api_number) ? savedParams.api_number.join('\n') : savedParams.api_number) : '',
      };
      
      setSearchParams(convertedParams);
      
        // Sync input values with restored search params
        setInputValues({
          operator_name: convertedParams.operator_name || '',
          operator_number: convertedParams.operator_number || '',
          lease_name: convertedParams.lease_name || '',
          lease_number: convertedParams.lease_number || '',
          county: convertedParams.county || '',
          api_number: convertedParams.api_number || '',
        });
        
        // Restore well group filters
        if (savedParams.wellGroupFilters) {
          setWellGroupFilters(savedParams.wellGroupFilters);
        }
        
      // Restore date filters
        if (savedParams.activityDateFilters) {
          setActivityDateFilters(savedParams.activityDateFilters);
        }
        if (savedParams.wellDateFilters) {
          setWellDateFilters(savedParams.wellDateFilters);
        }
        
        // Restore dropdown expansion states
        if (savedParams.dateFiltersExpanded) {
          setDateFiltersExpanded(savedParams.dateFiltersExpanded);
        }
        if (savedParams.mainFiltersExpanded) {
          setMainFiltersExpanded(savedParams.mainFiltersExpanded);
        }
        if (savedParams.otherFiltersExpanded) {
          setOtherFiltersExpanded(savedParams.otherFiltersExpanded);
        }
        if (savedParams.multiLineEnabled) {
          setMultiLineEnabled(savedParams.multiLineEnabled);
        }
        
        showNotification('Saved search loaded successfully! You can now run the search or modify the parameters.', 'success');
      }, 100); // Small delay to ensure session data is cleared
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, searchData.clearSearchSession, setSearchParams, setInputValues, setActivityDateFilters, setWellDateFilters, setWellGroupFilters, setDateFiltersExpanded, setMainFiltersExpanded, setOtherFiltersExpanded, setMultiLineEnabled]);


  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  // Convert activeTab number to tab type string for map
  const getActiveTabType = () => {
    const currentTableType = getCurrentTableType();
    // Map special tabs to 'wells' as default, or use actual table type
    if (currentTableType === 'matches' || currentTableType === 'relational') {
      return 'wells'; // Default to wells layer for Matches/Summary tabs
    }
    return currentTableType || 'wells';
  };

  const activeTabType = getActiveTabType();

  // ============================================================================
  // MAIN UI RENDER
  // ============================================================================
  // Check if we have search results to determine layout
  // Check if there's actual data (non-empty arrays), not just if keys exist
  const hasSearchResults = searchResults && searchResults.data && Object.keys(searchResults.data).length > 0 && 
    Object.values(searchResults.data).some(data => Array.isArray(data) && data.length > 0);

  // Shared About Search Data Dialog component
  const AboutSearchDataDialog = () => (
    <Dialog
      open={aboutSearchDialogOpen}
      onClose={() => setAboutSearchDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>About Search Data</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography paragraph>
            <strong>Production, Inspections, Violations, Severances, and Spills datasets</strong> – These datasets contain data from Jan 2022 through the most recent data update.
          </Typography>
          <Typography paragraph>
            <strong>Wells dataset</strong> – Contains all available well records.
          </Typography>
          <Typography paragraph>
            <strong>Database</strong> – Our database is updated regularly. There may be a delay between activity dates and when new data is added to the source databases, which varies by dataset. For example, with production data, there is at least a two-month lag time between occurrence months and when data is released.
          </Typography>
          <Typography paragraph>
            <strong>Data Quality</strong> – We put maximum effort to deliver high quality data and aggregate datasets. Certain datasets may not contain complete information, such as missing dates, location data, or operator identification. For best results, we suggest conducting multiple searches using different criteria. For example, if searching by operator number, also search by operator name; if searching specific leases, also conduct a county-wide search to compare results.
          </Typography>
          <Typography paragraph>
            <strong>More Information</strong> – To learn more about individual datasets, look for the <HelpOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle', mx: 0.5 }} /> icon in the individual dataset tabs in the Analytics section.
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAboutSearchDialogOpen(false)} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (hasSearchResults) {
    // Map-centric layout when results exist
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minHeight: 0, backgroundColor: 'background.paper' }}>
        {/* Hidden SearchForm for Refine Dialog */}
        <Box sx={{ display: 'none' }}>
          <SearchForm
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            inputValues={inputValues}
            setInputValues={setInputValues}
            mainFiltersExpanded={mainFiltersExpanded}
            setMainFiltersExpanded={setMainFiltersExpanded}
            multiLineEnabled={multiLineEnabled}
            setMultiLineEnabled={setMultiLineEnabled}
            otherFiltersExpanded={otherFiltersExpanded}
            setOtherFiltersExpanded={setOtherFiltersExpanded}
            wellGroupFilters={wellGroupFilters}
            setWellGroupFilters={setWellGroupFilters}
            activityDateFilters={activityDateFilters}
            setActivityDateFilters={setActivityDateFilters}
            wellDateFilters={wellDateFilters}
            setWellDateFilters={setWellDateFilters}
            dateFiltersExpanded={dateFiltersExpanded}
            setDateFiltersExpanded={setDateFiltersExpanded}
            loading={loading}
            searchResults={searchResults}
            resultsFilter={resultsFilter}
            handleInputChange={handleInputChange}
            handleInputBlur={handleInputBlur}
            toggleMultiLine={toggleMultiLine}
            toggleWellGroupFilter={toggleWellGroupFilter}
            handleSearch={handleSearchWrapper}
            executeSearch={runSearch}
            handleReset={handleResetWrapper}
            cancelSearch={cancelSearch}
            validateSearchParameters={validateSearchParameters}
            saveSearchDialogOpen={saveSearchDialogOpen}
            setSaveSearchDialogOpen={setSaveSearchDialogOpen}
            aboutSearchDialogOpen={aboutSearchDialogOpen}
            setAboutSearchDialogOpen={setAboutSearchDialogOpen}
            refineSearchDialogOpen={refineSearchDialogOpen}
            setRefineSearchDialogOpen={setRefineSearchDialogOpen}
            setWasRefineSearch={setWasRefineSearch}
            saveSearchName={saveSearchName}
            setSaveSearchName={setSaveSearchName}
            savingSearch={savingSearch}
            setSavingSearch={setSavingSearch}
            saveError={saveError}
            setSaveError={setSaveError}
            saveSuccess={saveSuccess}
            setSaveSuccess={setSaveSuccess}
            handleSaveSearch={handleSaveSearchWrapper}
            tierInfo={tierInfo}
          />
        </Box>

        <MapViewContainer
          searchResults={resultsFilter.filteredResults}
          searchParams={searchParams}
          activeTabType={activeTabType}
          resultsFilter={resultsFilter}
          leaseFlagsMap={leaseFlagsMap}
          countyFlagsMap={countyFlagsMap}
          redFlagsCache={redFlagsCache}
          onNavigateToLeaseInSummary={handleNavigateToLeaseInSummary}
          navigateToLeaseInMapRef={navigateToLeaseInMapRef}
          analyticsPanel={
            <AnalyticsSection
              searchResults={resultsFilter.filteredResults}
              resultsFilter={resultsFilter}
              searchParams={searchParams}
              activityDateFilters={activityDateFilters}
              wellDateFilters={wellDateFilters}
              activeTab={activeTab}
              setActiveTab={analyticsData.setActiveTab}
              getCurrentTableType={getCurrentTableType}
              getCurrentTableData={getCurrentTableData}
              getCurrentTableCounts={getCurrentTableCounts}
              loading={loading}
              searchFormVisible={searchFormVisible}
              cancelSearch={cancelSearch}
              handleSearchWrapper={handleSearchWrapper}
              setEditSearchDialogOpen={setEditSearchDialogOpen}
              setRefineSearchDialogOpen={setRefineSearchDialogOpen}
              validateSearchParameters={validateSearchParameters}
              handleResetWrapper={handleResetWrapper}
              setSaveSearchDialogOpen={setSaveSearchDialogOpen}
              setAboutSearchDialogOpen={setAboutSearchDialogOpen}
              handleGenerateReport={handleGenerateReport}
              wellGroupingTableSort={wellGroupingTableSort}
              setWellGroupingTableSort={analyticsData.setWellGroupingTableSort}
              handleWellGroupingTableSort={handleWellGroupingTableSort}
              riskWellsTableSort={riskWellsTableSort}
              setRiskWellsTableSort={analyticsData.setRiskWellsTableSort}
              handleRiskWellsTableSort={handleRiskWellsTableSort}
              multipleRecordsTableSort={multipleRecordsTableSort}
              setMultipleRecordsTableSort={analyticsData.setMultipleRecordsTableSort}
              handleMultipleRecordsTableSort={handleMultipleRecordsTableSort}
              resetMultipleRecordsTableSort={analyticsData.resetMultipleRecordsTableSort}
              violationsByRuleTableSort={violationsByRuleTableSort}
              setViolationsByRuleTableSort={analyticsData.setViolationsByRuleTableSort}
              handleViolationsByRuleTableSort={handleViolationsByRuleTableSort}
              resetViolationsByRuleTableSort={analyticsData.resetViolationsByRuleTableSort}
              riskWellsFilter={riskWellsFilter}
              setRiskWellsFilter={analyticsData.setRiskWellsFilter}
              resetRiskWellsFilter={resetRiskWellsFilter}
              inactiveYearsBinSize={inactiveYearsBinSize}
              setInactiveYearsBinSize={analyticsData.setInactiveYearsBinSize}
              resetInactiveYearsBinSize={resetInactiveYearsBinSize}
              violationsFilter={violationsFilter}
              setViolationsFilter={analyticsData.setViolationsFilter}
              resetViolationsFilter={resetViolationsFilter}
              severancesFilter={severancesFilter}
              setSeverancesFilter={analyticsData.setSeverancesFilter}
              resetSeverancesFilter={resetSeverancesFilter}
              inspectionsFilter={inspectionsFilter}
              setInspectionsFilter={analyticsData.setInspectionsFilter}
              resetInspectionsFilter={resetInspectionsFilter}
              hiddenWellGroups={hiddenWellGroups}
              setHiddenWellGroups={analyticsData.setHiddenWellGroups}
              resetHiddenWellGroups={resetHiddenWellGroups}
              hiddenViolationsLines={hiddenViolationsLines}
              setHiddenViolationsLines={analyticsData.setHiddenViolationsLines}
              resetHiddenViolationsLines={resetHiddenViolationsLines}
              hiddenReinspectionLines={hiddenReinspectionLines}
              setHiddenReinspectionLines={analyticsData.setHiddenReinspectionLines}
              resetHiddenReinspectionLines={resetHiddenReinspectionLines}
              hiddenSeverancesLines={hiddenSeverancesLines}
              setHiddenSeverancesLines={analyticsData.setHiddenSeverancesLines}
              resetHiddenSeverancesLines={resetHiddenSeverancesLines}
              hiddenSeveranceVolumeLines={hiddenSeveranceVolumeLines}
              setHiddenSeveranceVolumeLines={analyticsData.setHiddenSeveranceVolumeLines}
              resetHiddenSeveranceVolumeLines={resetHiddenSeveranceVolumeLines}
              hiddenInspectionLines={hiddenInspectionLines}
              setHiddenInspectionLines={analyticsData.setHiddenInspectionLines}
              resetHiddenInspectionLines={resetHiddenInspectionLines}
              hiddenProductionLines={hiddenProductionLines}
              setHiddenProductionLines={analyticsData.setHiddenProductionLines}
              resetHiddenProductionLines={resetHiddenProductionLines}
              hiddenFlaringLines={hiddenFlaringLines}
              setHiddenFlaringLines={analyticsData.setHiddenFlaringLines}
              resetHiddenFlaringLines={resetHiddenFlaringLines}
              hiddenFuelGasLines={hiddenFuelGasLines}
              setHiddenFuelGasLines={analyticsData.setHiddenFuelGasLines}
              resetHiddenFuelGasLines={resetHiddenFuelGasLines}
              hiddenSpillLines={hiddenSpillLines}
              setHiddenSpillLines={analyticsData.setHiddenSpillLines}
              resetHiddenSpillLines={resetHiddenSpillLines}
              hiddenSpillVolumeLines={hiddenSpillVolumeLines}
              setHiddenSpillVolumeLines={analyticsData.setHiddenSpillVolumeLines}
              resetHiddenSpillVolumeLines={resetHiddenSpillVolumeLines}
              productionTableSort={analyticsData.productionTableSort}
              handleProductionTableSort={analyticsData.handleProductionTableSort}
              resetProductionTableSort={analyticsData.resetProductionTableSort}
              inspectionsTableSort={analyticsData.inspectionsTableSort}
              handleInspectionsTableSort={analyticsData.handleInspectionsTableSort}
              resetInspectionsTableSort={analyticsData.resetInspectionsTableSort}
              violationsTableSort={analyticsData.violationsTableSort}
              handleViolationsTableSort={analyticsData.handleViolationsTableSort}
              resetViolationsTableSort={analyticsData.resetViolationsTableSort}
              severancesTableSort={analyticsData.severancesTableSort}
              handleSeverancesTableSort={analyticsData.handleSeverancesTableSort}
              resetSeverancesTableSort={analyticsData.resetSeverancesTableSort}
              spillsTableSort={analyticsData.spillsTableSort}
              handleSpillsTableSort={analyticsData.handleSpillsTableSort}
              resetSpillsTableSort={analyticsData.resetSpillsTableSort}
              getAllUniqueOperatorNames={getAllUniqueOperatorNames}
              setOperatorNamesDialogOpen={setOperatorNamesDialogOpen}
              tierInfo={tierInfo}
              onGenerateReport={handleGenerateReport}
              isGeneratingReport={isGeneratingReport}
              formatTableResult={formatTableResult}
              getUniqueOperatorNames={getAllUniqueOperatorNames}
              onClearMapSelection={() => {
                if (clearMapSelectionRef.current) {
                  clearMapSelectionRef.current();
                }
              }}
              onRegisterClearCallback={(callback) => {
                clearAnalyticsSelectionRef.current = callback;
              }}
              onZoomToData={() => {
                if (zoomToDataRef.current) {
                  zoomToDataRef.current();
                }
              }}
              onRegisterNavigateToLeaseInSummary={(callback) => {
                navigateToLeaseInSummaryRef.current = callback;
              }}
              navigateToLeaseInMapRef={navigateToLeaseInMapRef}
            />
          }
          analyticsPanelOpen={analyticsPanelOpen}
          setAnalyticsPanelOpen={setAnalyticsPanelOpen}
          onClearAnalyticsSelection={handleClearAnalyticsSelection}
          onClearMapSelectionRef={(callback) => { clearMapSelectionRef.current = callback; }}
          onZoomToDataRef={(callback) => { zoomToDataRef.current = callback; }}
        />

        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={notification.duration}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 1300 }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>

        {/* Operator Names Dialog */}
        <Dialog
          open={operatorNamesDialogOpen}
          onClose={() => setOperatorNamesDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Unique Operator Names
              </Typography>
              <IconButton
                onClick={() => setOperatorNamesDialogOpen(false)}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing unique operator names from search results across all tables
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {getAllUniqueOperatorNames().length > 0 ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Total: {getAllUniqueOperatorNames().length.toLocaleString()} unique operators</strong>
                  </Typography>
                  <List dense>
                    {getAllUniqueOperatorNames().map((operator, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText primary={operator} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Typography>No operator names found in search results</Typography>
              )}
            </Box>
          </DialogContent>
        </Dialog>

        {/* About Search Data Dialog */}
        <AboutSearchDataDialog />

        {/* Edit Search Dialog */}
        <Dialog
          open={editSearchDialogOpen}
          onClose={() => setEditSearchDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Edit Search Parameters
            <Tooltip title="Undo Edit Search Parameters" arrow>
              <IconButton
                onClick={() => {
                  if (originalSearchParams) {
                    setSearchParams(originalSearchParams);
                    setInputValues({
                      operator_name: originalSearchParams.operator_name || '',
                      operator_number: originalSearchParams.operator_number || '',
                      lease_name: originalSearchParams.lease_name || '',
                      lease_number: originalSearchParams.lease_number || '',
                      county: originalSearchParams.county || '',
                    });
                  }
                }}
                size="small"
                color="primary"
              >
                <UndoIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <SearchForm
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              inputValues={inputValues}
              setInputValues={setInputValues}
              mainFiltersExpanded={mainFiltersExpanded}
              setMainFiltersExpanded={setMainFiltersExpanded}
              multiLineEnabled={multiLineEnabled}
              setMultiLineEnabled={setMultiLineEnabled}
              otherFiltersExpanded={otherFiltersExpanded}
              setOtherFiltersExpanded={setOtherFiltersExpanded}
              wellGroupFilters={wellGroupFilters}
              setWellGroupFilters={setWellGroupFilters}
              activityDateFilters={activityDateFilters}
              setActivityDateFilters={setActivityDateFilters}
              wellDateFilters={wellDateFilters}
              setWellDateFilters={setWellDateFilters}
              dateFiltersExpanded={dateFiltersExpanded}
              setDateFiltersExpanded={setDateFiltersExpanded}
              loading={loading}
              searchResults={searchResults}
              handleInputChange={handleInputChange}
              handleInputBlur={handleInputBlur}
              toggleMultiLine={toggleMultiLine}
              toggleWellGroupFilter={toggleWellGroupFilter}
              handleSearch={() => {
                setEditSearchDialogOpen(false);
                handleSearchWrapper();
              }}
              handleReset={() => {
                setEditSearchDialogOpen(false);
                handleResetWrapper();
              }}
              cancelSearch={cancelSearch}
              validateSearchParameters={validateSearchParameters}
              saveSearchDialogOpen={saveSearchDialogOpen}
              setSaveSearchDialogOpen={setSaveSearchDialogOpen}
              aboutSearchDialogOpen={aboutSearchDialogOpen}
              setAboutSearchDialogOpen={setAboutSearchDialogOpen}
              refineSearchDialogOpen={refineSearchDialogOpen}
              setRefineSearchDialogOpen={setRefineSearchDialogOpen}
              setWasRefineSearch={setWasRefineSearch}
              saveSearchName={saveSearchName}
              setSaveSearchName={setSaveSearchName}
              savingSearch={savingSearch}
              setSavingSearch={setSavingSearch}
              saveError={saveError}
              setSaveError={setSaveError}
              saveSuccess={saveSuccess}
              setSaveSuccess={setSaveSuccess}
              handleSaveSearch={handleSaveSearchWrapper}
              tierInfo={tierInfo}
              isDialogMode={true}
              originalSearchParams={originalSearchParams}
              onDialogClose={() => setEditSearchDialogOpen(false)}
            />
          </DialogContent>
          <DialogActions sx={{ px: 1, pb: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="text" 
                onClick={() => {
                  if (originalSearchParams) {
                    setSearchParams(originalSearchParams);
                    setInputValues({
                      operator_name: originalSearchParams.operator_name || '',
                      operator_number: originalSearchParams.operator_number || '',
                      lease_name: originalSearchParams.lease_name || '',
                      lease_number: originalSearchParams.lease_number || '',
                      county: originalSearchParams.county || '',
                      api_number: originalSearchParams.api_number || '',
                    });
                  }
                  setEditSearchDialogOpen(false);
                }}
                color="primary"
              >
                Cancel
              </Button>
              <Button 
                variant="contained"
                onClick={() => {
                  handleSearchWrapper();
                  setEditSearchDialogOpen(false);
                }}
                disabled={!searchParams.state || !validateSearchParameters().isValid}
                color="primary"
              >
                Search
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* PDF Generation Loading Backdrop */}
        <Backdrop
          sx={{
            color: '#fff', 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            pointerEvents: 'auto'
          }}
          open={isGeneratingReport}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: 2
          }}>
            <CircularProgress color="inherit" size={60} />
            <Typography variant="h6" sx={{ color: 'white', textAlign: 'center', maxWidth: 300 }}>
              {isCancellingReport ? 'Cancelling PDF Generation...' : 'Generating Desktop Diligence PDF Report...'}
            </Typography>
            {!isCancellingReport && (
              <>
                <Typography variant="body1" sx={{ color: 'white', textAlign: 'center', maxWidth: 300, fontWeight: 500 }}>
                  {reportProgress || 'Initializing...'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', textAlign: 'center', maxWidth: 300, opacity: 0.8 }}>
                  Stay on this page for best results.
                </Typography>
              </>
            )}
            {!isCancellingReport && (
              <Button 
                variant="outlined"
                onClick={handleCancelReport}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Backdrop>
      </Box>
    );
  }

  // Traditional layout when no search results
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box id="unified-search">
          <Typography
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              mb: 1,
            }}
          >
            Texas
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontWeight: 750,
              fontSize: { xs: '2rem', md: '2.6rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.05,
              color: INK,
              mb: 1,
            }}
          >
            Search
          </Typography>
          <Typography sx={{ color: MUTED, maxWidth: 560, mb: 3, lineHeight: 1.6 }}>
            Choose the datasets and the operator, lease, county, or area. The map and report open when the search returns records.
          </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                      variant={loading ? 'outlined' : 'contained'}
                      onClick={loading ? cancelSearch : (searchFormVisible ? handleSearchWrapper : () => setEditSearchDialogOpen(true))}
                      disabled={!loading && (!searchParams.state || !validateSearchParameters().isValid)}
                      color="primary"
                      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                      sx={loading ? { textTransform: 'none', fontWeight: 700, borderRadius: 999 } : { ...goldButtonSx, px: 3 }}
                    >
                      {loading ? 'Cancel' : (searchFormVisible ? 'Search' : 'Edit')}
                </Button>
                    {hasSearchResults && (
                   <Button
                  variant="outlined" 
                        onClick={() => setRefineSearchDialogOpen(true)}
                        color="primary"
                >
                        Refine
                   </Button>
                    )}
            </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Save Search" arrow>
                      <span>
                        <IconButton
                        size="small"
                          color="primary"
                        onClick={() => setSaveSearchDialogOpen(true)}
                        disabled={!searchParams.state || !validateSearchParameters().isValid || (tierInfo && !tierInfo.can_save_searches)}
                        sx={{
                          opacity: (tierInfo && !tierInfo.can_save_searches) ? 0.5 : 1,
                          cursor: (tierInfo && !tierInfo.can_save_searches) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <SaveIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Export Analytics as a PDF Report" arrow>
                      <span>
                        <IconButton
                        size="small"
                          color="primary"
                        onClick={handleGenerateReport}
                        disabled={isGeneratingReport || (tierInfo && !tierInfo.can_generate_reports) || !hasSearchResults}
                        sx={{
                          cursor: (tierInfo && !tierInfo.can_generate_reports) || !hasSearchResults ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <DownloadIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="About Search" arrow>
                        <IconButton
                        size="small"
                          color="primary"
                        onClick={() => setAboutSearchDialogOpen(true)}
                        >
                        <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Entire Search" arrow>
                        <IconButton
                        size="small"
                          color="error"
                        onClick={handleResetWrapper}
                        >
                          <RestartAltIcon />
                        </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                
          {/* No Results Message */}
          {searchResults && !hasSearchResults && (
            <Box sx={{ p: 2.5, mb: 3, border: `1px solid ${LINE}`, borderRadius: 2, bgcolor: '#fff' }}>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No results</Typography>
              <Typography sx={{ color: MUTED }}>
                Nothing matched that search. Adjust the criteria and run it again.
              </Typography>
            </Box>
          )}
                
        {/* Search Form */}
                  <Box sx={{ 
          visibility: searchFormVisible ? 'visible' : 'hidden',
          height: searchFormVisible ? 'auto' : 0,
                 overflow: 'hidden'
               }}>
          <SearchForm
          // State props
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          inputValues={inputValues}
          setInputValues={setInputValues}
          mainFiltersExpanded={mainFiltersExpanded}
          setMainFiltersExpanded={setMainFiltersExpanded}
          multiLineEnabled={multiLineEnabled}
          setMultiLineEnabled={setMultiLineEnabled}
          otherFiltersExpanded={otherFiltersExpanded}
          setOtherFiltersExpanded={setOtherFiltersExpanded}
          wellGroupFilters={wellGroupFilters}
          setWellGroupFilters={setWellGroupFilters}
          activityDateFilters={activityDateFilters}
          setActivityDateFilters={setActivityDateFilters}
          wellDateFilters={wellDateFilters}
          setWellDateFilters={setWellDateFilters}
          dateFiltersExpanded={dateFiltersExpanded}
          setDateFiltersExpanded={setDateFiltersExpanded}
          loading={loading}
          
          // Expand/collapse props
          searchFormExpanded={searchFormExpanded}
          setSearchFormExpanded={setSearchFormExpanded}
          
          // Search results for filter options
          searchResults={searchResults}
          resultsFilter={resultsFilter}
          
          // Handler props
          handleInputChange={handleInputChange}
          handleInputBlur={handleInputBlur}
          toggleMultiLine={toggleMultiLine}
          toggleWellGroupFilter={toggleWellGroupFilter}
          handleSearch={handleSearchWrapper}
          executeSearch={runSearch}
          handleReset={handleResetWrapper}
          cancelSearch={cancelSearch}
          validateSearchParameters={validateSearchParameters}
          
          // Dialog props
          saveSearchDialogOpen={saveSearchDialogOpen}
          setSaveSearchDialogOpen={setSaveSearchDialogOpen}
          aboutSearchDialogOpen={aboutSearchDialogOpen}
          setAboutSearchDialogOpen={setAboutSearchDialogOpen}
          refineSearchDialogOpen={refineSearchDialogOpen}
          setRefineSearchDialogOpen={setRefineSearchDialogOpen}
          setWasRefineSearch={setWasRefineSearch}
          
          // Save search props
          saveSearchName={saveSearchName}
          setSaveSearchName={setSaveSearchName}
          savingSearch={savingSearch}
          setSavingSearch={setSavingSearch}
          saveError={saveError}
          setSaveError={setSaveError}
          saveSuccess={saveSuccess}
          setSaveSuccess={setSaveSuccess}
          handleSaveSearch={handleSaveSearchWrapper}
          tierInfo={tierInfo}
        />
                  </Box>



        {/* Notification Snackbar */}
        <Snackbar
          open={notification.open}
          autoHideDuration={notification.duration}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 1300 }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>

        {/* Operator Names Dialog */}
      <Dialog
          open={operatorNamesDialogOpen}
          onClose={() => setOperatorNamesDialogOpen(false)}
          maxWidth="md"
        fullWidth
        >
          <DialogTitle>
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Unique Operator Names
                   </Typography>
                        <IconButton
                onClick={() => setOperatorNamesDialogOpen(false)}
                        size="small"
                        >
                <CloseIcon />
                        </IconButton>
                  </Box>
        </DialogTitle>
        <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Showing unique operator names from search results across all tables
                  </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {getAllUniqueOperatorNames().length > 0 ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Total: {getAllUniqueOperatorNames().length.toLocaleString()} unique operators</strong>
                  </Typography>
                  <List dense>
                    {getAllUniqueOperatorNames().map((operator, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText primary={operator} />
                      </ListItem>
                    ))}
                  </List>
                  </Box>
              ) : (
                <Typography>No operator names found in search results</Typography>
              )}
                </Box>
        </DialogContent>
      </Dialog>

      {/* About Search Data Dialog */}
      <AboutSearchDataDialog />

      {/* Edit Search Dialog */}
      <Dialog
        open={editSearchDialogOpen}
        onClose={() => setEditSearchDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Search Parameters
          <Tooltip title="Undo Edit Search Parameters" arrow>
                        <IconButton
                        onClick={() => {
                // Reset to the original search parameters from when search was completed
                if (originalSearchParams) {
                  setSearchParams(originalSearchParams);
                  // Also reset input values to match
                  setInputValues({
                    operator_name: originalSearchParams.operator_name || '',
                    operator_number: originalSearchParams.operator_number || '',
                    lease_name: originalSearchParams.lease_name || '',
                    lease_number: originalSearchParams.lease_number || '',
                    county: originalSearchParams.county || '',
                    api_number: originalSearchParams.api_number || '',
                  });
                }
              }}
                        size="small"
                          color="primary"
                        >
              <UndoIcon />
                        </IconButton>
                    </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <SearchForm
            // State props
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            inputValues={inputValues}
            setInputValues={setInputValues}
            mainFiltersExpanded={mainFiltersExpanded}
            setMainFiltersExpanded={setMainFiltersExpanded}
            multiLineEnabled={multiLineEnabled}
            setMultiLineEnabled={setMultiLineEnabled}
            otherFiltersExpanded={otherFiltersExpanded}
            setOtherFiltersExpanded={setOtherFiltersExpanded}
            wellGroupFilters={wellGroupFilters}
            setWellGroupFilters={setWellGroupFilters}
            activityDateFilters={activityDateFilters}
            setActivityDateFilters={setActivityDateFilters}
            wellDateFilters={wellDateFilters}
            setWellDateFilters={setWellDateFilters}
            dateFiltersExpanded={dateFiltersExpanded}
            setDateFiltersExpanded={setDateFiltersExpanded}
            loading={loading}
            
            // Search results for filter options
            searchResults={searchResults}
            resultsFilter={resultsFilter}
            
            // Handler props
            handleInputChange={handleInputChange}
            handleInputBlur={handleInputBlur}
            toggleMultiLine={toggleMultiLine}
            toggleWellGroupFilter={toggleWellGroupFilter}
            handleSearch={() => {
              setEditSearchDialogOpen(false);
              handleSearchWrapper();
            }}
            executeSearch={runSearch}
            handleReset={() => {
              setEditSearchDialogOpen(false);
              handleResetWrapper();
            }}
            cancelSearch={cancelSearch}
            validateSearchParameters={validateSearchParameters}
            
            // Dialog props
            saveSearchDialogOpen={saveSearchDialogOpen}
            setSaveSearchDialogOpen={setSaveSearchDialogOpen}
            aboutSearchDialogOpen={aboutSearchDialogOpen}
            setAboutSearchDialogOpen={setAboutSearchDialogOpen}
            refineSearchDialogOpen={refineSearchDialogOpen}
            setRefineSearchDialogOpen={setRefineSearchDialogOpen}
            setWasRefineSearch={setWasRefineSearch}
            
            // Save search props
            saveSearchName={saveSearchName}
            setSaveSearchName={setSaveSearchName}
            savingSearch={savingSearch}
            setSavingSearch={setSavingSearch}
            saveError={saveError}
            setSaveError={setSaveError}
            saveSuccess={saveSuccess}
            setSaveSuccess={setSaveSuccess}
            handleSaveSearch={handleSaveSearchWrapper}
            tierInfo={tierInfo}
            
            // Dialog mode
            isDialogMode={true}
            originalSearchParams={originalSearchParams}
            onDialogClose={() => setEditSearchDialogOpen(false)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="text" 
                        onClick={() => {
                // Cancel - reset to original parameters and close dialog
                if (originalSearchParams) {
                  setSearchParams(originalSearchParams);
                  setInputValues({
                    operator_name: originalSearchParams.operator_name || '',
                    operator_number: originalSearchParams.operator_number || '',
                    lease_name: originalSearchParams.lease_name || '',
                    lease_number: originalSearchParams.lease_number || '',
                    county: originalSearchParams.county || '',
                    api_number: originalSearchParams.api_number || '',
                  });
                }
                setEditSearchDialogOpen(false);
              }}
                          color="primary"
            >
            Cancel
          </Button>
          <Button 
            variant="contained"
                        onClick={() => {
                // Search with current parameters
                handleSearchWrapper();
                setEditSearchDialogOpen(false);
              }}
              disabled={!searchParams.state || !validateSearchParameters().isValid}
                          color="primary"
            >
              Search
          </Button>
                  </Box>
        </DialogActions>
      </Dialog>

      {activityDateConfirmDialog}

      {/* PDF Generation Loading Backdrop */}
      <Backdrop
        sx={{
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          pointerEvents: 'auto'
        }}
        open={isGeneratingReport}
      >
                  <Box sx={{ 
                    display: 'flex', 
          flexDirection: 'column', 
                    alignItems: 'center', 
          gap: 2
        }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ color: 'white', textAlign: 'center', maxWidth: 300 }}>
            {isCancellingReport ? 'Cancelling PDF Generation...' : 'Generating Desktop Diligence PDF Report...'}
                  </Typography>
          {!isCancellingReport && (
            <>
              <Typography variant="body1" sx={{ color: 'white', textAlign: 'center', maxWidth: 300, fontWeight: 500 }}>
                {reportProgress || 'Initializing...'}
                </Typography>
              <Typography variant="body2" sx={{ color: 'white', textAlign: 'center', maxWidth: 300, opacity: 0.8 }}>
                Stay on this page for best results.
                </Typography>
              </>
            )}
          {!isCancellingReport && (
          <Button 
              variant="outlined"
              onClick={handleCancelReport}
        sx={{
            color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Cancel
            </Button>
          )}
          </Box>
      </Backdrop>

          </Box>
    </Container>
  );
};

export default Search;
